/**
 * Homepage Course Window pick (spec 010 HOME-V3 §C): the area a visitor tapped as
 * "your weakest" on the example student. It is a sentence for the course home's
 * first-run hero (`You guessed X is your weakest.`), never data: it touches no
 * ordering, score or form. Self-healing like the taster store: malformed state is
 * cleared and read as absent; throwing storage never breaks the page.
 */
import { DOMAIN_LABELS, type SatDomain } from '@/api/sat';

export const SAT_OPEN_PICK_KEY = 'satOpenPick';

export interface SatOpenPickState {
  version: 1;
  domain: SatDomain;
  pickedAt: string; // ISO
}

/** The last pick whose write failed, so this session still counts it. */
let unpersisted: SatOpenPickState | null = null;

export function clearOpenPick(): void {
  unpersisted = null;
  try {
    localStorage.removeItem(SAT_OPEN_PICK_KEY);
  } catch {
    // storage unavailable: nothing to clear
  }
}

function isValid(value: unknown): value is SatOpenPickState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return v.version === 1 && typeof v.domain === 'string' && v.domain in DOMAIN_LABELS && typeof v.pickedAt === 'string';
}

export function loadOpenPick(): SatOpenPickState | null {
  if (unpersisted) return unpersisted;
  let raw: string | null;
  try {
    raw = localStorage.getItem(SAT_OPEN_PICK_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isValid(parsed)) throw new Error('malformed pick state');
    return parsed;
  } catch {
    clearOpenPick();
    return null;
  }
}

/** Replaces any earlier pick. Never throws. `pickedAt` is injected: callers own the clock. */
export function recordOpenPick(domain: SatDomain, pickedAt: string): SatOpenPickState {
  const state: SatOpenPickState = { version: 1, domain, pickedAt };
  try {
    localStorage.setItem(SAT_OPEN_PICK_KEY, JSON.stringify(state));
    unpersisted = null;
  } catch {
    unpersisted = state;
  }
  return state;
}
